import { crdt } from '@w/crdt'
import { useEditorKeys, isFocusWithin } from '@w/utils-fe'
import {
  FormatFlag,
  getTextFromChars,
  makeChars,
  type Block,
} from '@w/content-blocks'
import {
  BlockEditor,
  type BlockEditorHandle,
  type FileHandler,
} from '@w/content-blocks-editor'
import { useRef, useState } from 'react'
import type { Example } from './lexicon-model'
import { findTermSpan } from '../word-generation/word-positions'

const noFiles: FileHandler = {
  getUrl: (src) => src,
  upload: () =>
    Promise.reject(new Error('Inline examples do not accept files')),
}

export function InlineExampleEditor(props: {
  spaceId: string
  example: Example
  needle: string
  ariaLabel: string
  testId: string
  secondary?: boolean
  onCommit: (example: Example) => boolean
  onCancel: () => void
}) {
  const handleRef = useRef<BlockEditorHandle | null>(null)
  const [error, setError] = useState<string | null>(null)
  const committingRef = useRef(false)
  const [blocks] = useState(() => _exampleBlocks(props.example))
  const [doc] = useState(() => crdt.create({ content: { children: blocks as unknown as Parameters<typeof crdt.create>[0] } }))

  const commit = () => {
    if (committingRef.current) return
    committingRef.current = true
    const text = _plainText(handleRef.current?.getSnapshot().blocks ?? blocks)
    const node = findTermSpan(props.needle, text)
    if (!node) {
      setError(`Example must contain “${props.needle}”.`)
      committingRef.current = false
      return
    }
    if (!props.onCommit({ ...props.example, text, node })) {
      setError('Example has an invalid highlighted span.')
      committingRef.current = false
    }
  }

  const handleKeyDown = useEditorKeys({ onEnter: commit, onEscape: props.onCancel })

  return (
    // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- composite editor wrapper coordinates blur/keyboard events from interactive descendants
    <div
      className="flex flex-col gap-1"
      role="group"
      data-testid={`${props.testId}-rich`}
      data-formatted-node="underline"
      onBlur={(event) => {
        if (isFocusWithin(event)) return
        commit()
      }}
      onKeyDown={handleKeyDown}
    >
      <BlockEditor
        spaceId={props.spaceId}
        doc={doc}
        focusOnMount
        fileHandler={noFiles}
        onChange={() => {
          setError(null)
        }}
        onReady={(handle) => {
          if (!handleRef.current) handle.focusTitle({ selectText: false })
          handleRef.current = handle
        }}
        placeholder={null}
        testIds={{ root: props.testId }}
        className={`bg-surface-2 ring-primary/30 rounded-md px-2 py-1 ring-1 outline-none ${
          props.secondary
            ? 'text-on-surface-secondary text-xs leading-relaxed'
            : 'text-on-surface text-sm leading-relaxed'
        }`}
      />
      {error ? (
        <p className="text-error text-sm" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}

function _exampleBlocks(example: Example): Block[] {
  const [offset, length] = example.node
  return [
    {
      id: 'example-p',
      type: 'p',
      children: [
        makeChars({
          id: 'example-before',
          text: example.text.slice(0, offset),
        }),
        makeChars({
          id: 'example-node',
          text: example.text.slice(offset, offset + length),
          format: FormatFlag.UNDERLINE,
        }),
        makeChars({
          id: 'example-after',
          text: example.text.slice(offset + length),
        }),
      ],
    },
  ]
}

function _plainText(blocks: Block[]): string {
  return blocks
    .map((block) => {
      if (block.type === 'text') return block.text
      if (block.type === 'chars') return getTextFromChars(block)
      if (block.type === 'br') return '\n'
      return 'children' in block && Array.isArray(block.children)
        ? _plainText(block.children as Block[])
        : ''
    })
    .join('')
}
